import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from 'react'

interface UsePriceFilterParams {
	minValue: string
	maxValue: string
	checkedFrom: string | undefined
	checkedTo: string | undefined
	onSubmit: (from: string, to: string) => void
}

export const usePriceFilter = ({ minValue, maxValue, checkedFrom, checkedTo, onSubmit }: UsePriceFilterParams) => {
	const [priceFrom, setPriceFrom] = useState(checkedFrom ?? minValue)
	const [priceTo, setPriceTo] = useState(checkedTo ?? maxValue)

	const submittedValues = useRef<{ from: string; to: string } | null>(null)

	useEffect(() => {
		const newFrom = checkedFrom ?? minValue
		const newTo = checkedTo ?? maxValue

		if (submittedValues.current) {
			const { from: subFrom, to: subTo } = submittedValues.current
			submittedValues.current = null

			if (subFrom === newFrom && subTo === newTo) return
		}

		setPriceFrom(newFrom)
		setPriceTo(newTo)
	}, [checkedFrom, checkedTo, minValue, maxValue])

	const normalizePriceInput = (value: string): string => value.replace(/\D/g, '')

	const handleFromChange = (e: ChangeEvent<HTMLInputElement>) => {
		setPriceFrom(normalizePriceInput(e.target.value))
	}

	const handleToChange = (e: ChangeEvent<HTMLInputElement>) => {
		setPriceTo(normalizePriceInput(e.target.value))
	}

	const handleBlur = () => {
		const min = Number(minValue)
		const max = Number(maxValue)

		let fromNum = Number(priceFrom || minValue)
		let toNum = Number(priceTo || maxValue)

		if (fromNum < min) fromNum = min
		if (fromNum > max) fromNum = max
		if (toNum < min) toNum = min
		if (toNum > max) toNum = max

		if (fromNum > toNum) {
			const temp = fromNum
			fromNum = toNum
			toNum = temp
		}

		const finalFrom = String(fromNum)
		const finalTo = String(toNum)

		setPriceFrom(finalFrom)
		setPriceTo(finalTo)

		submittedValues.current = { from: finalFrom, to: finalTo }
		onSubmit(finalFrom, finalTo)
	}

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault()
			;(e.target as HTMLInputElement).blur()
		}
	}

	return {
		priceFrom,
		priceTo,
		handleFromChange,
		handleToChange,
		handleBlur,
		handleKeyDown
	}
}
